import { useEffect } from 'react'

import { useAccount, useSummary } from '@/modules/account'
import { useWebsocketContext } from '@/modules/websocketContext'
import { humanizeDate } from '@/utils'
import { BANKRUPTCY_STATUSES } from './constants'
import { useFilingsStore } from './stores/filings'
import { useProjectScreeningStore } from './stores/projectScreening'
import type { Filing, FilingDTO } from './stores/types'

export const useFilings = () => useFilingsStore((state) => state.filings)

export const useFiling = (id: number) =>
  useFilingsStore((state) => state.filings.find((filing) => filing.id === id))

export const useCurrentFiling = () =>
  useFilingsStore((state) => state.filingDetail)

export const useFilingDetail = (id: number) => {
  const filling = useFilingsStore((state) => state.detailsById[id])
  const { setCurrentFilingDetail } = useFilingsStore((state) => state.actions)
  setCurrentFilingDetail(id)
  return filling
}

export const useFilingDetailStatus = (id: number) =>
  useFilingsStore((state) => state.detailStatusById[id] ?? 'loading')

export const useLoadingFilings = () =>
  useFilingsStore((state) => state.loadingFilings)

export const useFilingsMutating = () =>
  useFilingsStore((state) => state.mutating)

export const useFilingMutations = () =>
  useFilingsStore((state) => state.mutations)

export const useProjectScreening = () =>
  useProjectScreeningStore((state) => state.screening)

export const useLoadingProjectScreening = () =>
  useProjectScreeningStore((state) => state.loading)

export const useFetchProjectScreening = (slug: string) => {
  const { fetchScreening } = useProjectScreeningStore((state) => state.loaders)
  const { reset } = useProjectScreeningStore((state) => state.actions)

  useEffect(() => {
    fetchScreening(slug)
    return reset
  }, [slug])
}

export const useFetchFilings = () => {
  const { fetchFilings } = useFilingsStore((state) => state.loaders)

  useEffect(() => {
    fetchFilings()
  }, [])
}

// Refetch when the status moves: a finished screen brings the breakdown.
export const useFetchFilingDetail = (id: number, status?: string) => {
  const { fetchFilingDetail } = useFilingsStore((state) => state.loaders)

  useEffect(() => {
    fetchFilingDetail(id)
  }, [id, status])
}

interface FilingEvent {
  action: string
  filing: FilingDTO
}

export const useScreenerWebsocket = () => {
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const { upsertFiling } = useFilingsStore((state) => state.actions)

  useEffect(() => {
    if (!isReady) return

    const subscription = subscribe(
      { channel: 'AccountChannel' },
      {
        received: (data: unknown) => {
          const event = data as FilingEvent
          if (event.action === 'UPDATE_FILING') upsertFiling(event.filing)
        },
      },
    )

    return () => unsubscribe(subscription)
  }, [isReady])
}

export const useIsScreenerEntitled = () =>
  !!useAccount()?.isToolsBankruptcyEnabled

export interface ScreeningLimits {
  credits: number
  creditsNextExpiry: string | null
  outOfCredits: boolean
  overPageLimit: boolean
}

export const useScreeningLimits = (filing?: Filing): ScreeningLimits | null => {
  const account = useAccount()
  const summary = useSummary()
  if (!summary) return null

  const unpaid = !filing || filing.status === BANKRUPTCY_STATUSES.draft
  const expiry = summary.screeningsCreditsNextExpiry

  return {
    credits: summary.screeningsCredits,
    creditsNextExpiry: expiry ? humanizeDate(expiry) : expiry,
    outOfCredits: !account?.isDemo && unpaid && summary.screeningsCredits <= 0,
    overPageLimit: !!filing && filing.numberOfPages > filing.pageLimit,
  }
}
